import { useState, useEffect, useCallback, useRef } from 'react'
import { useLogto } from '@logto/react'
import { isLogtoEnabled, getLogtoApiResource } from '@/providers/LogtoProviderWrapper'

// Permission constants
export const PERMISSION_ADMIN = 'admin'

interface UsePermissionsResult {
  permissions: string[]
  isLoading: boolean
  hasPermission: (permission: string) => boolean
  isAdmin: boolean
  refreshPermissions: () => Promise<void>
}

/**
 * Extract user permissions from Logto access token.
 *
 * Development mode (Logto not configured): returns admin permission.
 * Production mode: extracts permissions from access token's scope claim.
 */
export function usePermissions(): UsePermissionsResult {
  const [permissions, setPermissions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Track if permissions have been fetched to prevent re-fetching
  const hasFetchedRef = useRef(false)
  const isFetchingRef = useRef(false)

  const logtoEnabled = isLogtoEnabled()

  // Always call useLogto to follow React hooks rules, but only use when enabled
  const logtoHook = useLogto()
  const isAuthenticated = logtoEnabled ? logtoHook.isAuthenticated : false
  const isAuthLoading = logtoEnabled ? logtoHook.isLoading : false
  const getAccessToken = logtoEnabled ? logtoHook.getAccessToken : undefined

  const apiResource = getLogtoApiResource()

  // Use refs to store latest values to avoid useCallback dependency issues
  // This prevents infinite loops caused by isAuthLoading changes during getAccessToken calls
  const isAuthLoadingRef = useRef(isAuthLoading)
  const isAuthenticatedRef = useRef(isAuthenticated)
  const getAccessTokenRef = useRef(getAccessToken)

  // Keep refs in sync
  isAuthLoadingRef.current = isAuthLoading
  isAuthenticatedRef.current = isAuthenticated
  getAccessTokenRef.current = getAccessToken

  const refreshPermissions = useCallback(async () => {
    // Development mode: grant admin permission
    if (!logtoEnabled) {
      setPermissions([PERMISSION_ADMIN])
      setIsLoading(false)
      hasFetchedRef.current = true
      return
    }

    // Already fetched successfully, don't re-fetch
    if (hasFetchedRef.current) {
      return
    }

    // Auth still loading: keep permissions loading too
    if (isAuthLoadingRef.current) {
      return
    }

    // Not authenticated: no permissions (but done loading)
    if (!isAuthenticatedRef.current || !getAccessTokenRef.current) {
      setPermissions([])
      setIsLoading(false)
      hasFetchedRef.current = true
      return
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return
    }

    try {
      isFetchingRef.current = true
      // Don't set isLoading here to avoid triggering re-renders during fetch

      if (!apiResource) {
        console.warn('Logto API resource not configured')
        setPermissions([])
        setIsLoading(false)
        hasFetchedRef.current = true
        return
      }

      // Get access token for API Resource
      const token = await getAccessTokenRef.current(apiResource)

      if (!token) {
        setPermissions([])
        setIsLoading(false)
        hasFetchedRef.current = true
        return
      }

      // Decode JWT to extract scope claim
      const parts = token.split('.')
      if (parts.length !== 3) {
        console.error('Invalid JWT format')
        setPermissions([])
        setIsLoading(false)
        hasFetchedRef.current = true
        return
      }

      // Decode payload (base64url)
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))

      // Extract scope claim (space-separated string)
      const scopeString = payload.scope || ''
      const scopes = scopeString.split(' ').filter((s: string) => s.length > 0)

      setPermissions(scopes)
      hasFetchedRef.current = true
    } catch (error) {
      console.error('Error extracting permissions from token:', error)
      setPermissions([])
      hasFetchedRef.current = true
    } finally {
      setIsLoading(false)
      isFetchingRef.current = false
    }
  }, [logtoEnabled, apiResource])  // Removed isAuthLoading, isAuthenticated, getAccessToken - use refs instead

  // Refresh permissions when auth state stabilizes (not during loading)
  useEffect(() => {
    // Only trigger when auth loading completes and we haven't fetched yet
    if (!isAuthLoading && !hasFetchedRef.current) {
      refreshPermissions()
    }
  }, [isAuthLoading, refreshPermissions])

  const hasPermission = useCallback(
    (permission: string) => permissions.includes(permission),
    [permissions]
  )

  const isAdmin = hasPermission(PERMISSION_ADMIN)

  return {
    permissions,
    isLoading,
    hasPermission,
    isAdmin,
    refreshPermissions,
  }
}
